import { Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { constants } from '../shared/constants/constants';

@Injectable({ providedIn: 'root' })
export class MockDataService {
    onCurrentConsumptionValue = new Subject();
    private timestamp_start = new Date();
    private test = false;
    private plugOff = false;

    private power_data = {
        power_5_min: null,
        power_1_hour: null,
        power_12_hours: null,
        power_24_hours: null,
    };

    private consumption_data = {
        hours: null,
        days: null,
        months: null,
    };

    constructor(private http: HttpClient) {}

    /**
     * Get live data from mock files
     */
    public getLiveData(
        offset: number,
        level: number,
        limit: number,
        interval: number
    ): Observable<any> {
        let dataset_file = '';
        let dataset;
        switch (level) {
            case 1:
                dataset_file = constants.demo.files.power5Seconds;
                if (this.power_data.power_5_min !== null) {
                    dataset = this.power_data.power_5_min;
                }
                break;
            case 2:
                dataset_file = constants.demo.files.power1Minute;
                if (this.power_data.power_1_hour !== null) {
                    dataset = this.power_data.power_1_hour;
                }
                break;
            case 3:
                dataset_file = constants.demo.files.power1Minute;
                if (this.power_data.power_12_hours !== null) {
                    dataset = this.power_data.power_12_hours;
                }
                break;
            case 4:
                dataset_file = constants.demo.files.power15Minutes;
                if (this.power_data.power_24_hours !== null) {
                    dataset = this.power_data.power_24_hours;
                }
                break;
        }

        if (dataset) {
            return of(dataset).pipe(
                map((value) => {
                    return this.filterByTime(value, offset, limit);
                }),
                catchError((error) => {
                    return throwError(error);
                })
            );
        }

        return this.http.get(`assets/data/demo/${dataset_file}.json`).pipe(
            map((value: any) => {
                const data_aligned = this.mapValues(value, offset, interval, level);
                switch (offset) {
                    case 5:
                        this.power_data.power_5_min = data_aligned;
                        break;
                    case 60:
                        this.power_data.power_1_hour = data_aligned;
                        break;
                    case 60 * 12:
                        this.power_data.power_12_hours = data_aligned;
                        break;
                    case 60 * 24:
                        this.power_data.power_24_hours = data_aligned;
                        break;
                }
                return this.filterByTime(data_aligned, offset, limit);
            }),
            catchError((error) => {
                if (error.status === 401) {
                    console.log('MockDataService error: ', error);
                }
                return throwError(error);
            })
        );
    }

    /**
     * Get the current consumption-alert status
     */
    public getHomeStateStatus(): Observable<any> {
        return this.http.get(`assets/data/demo/${constants.demo.files.homeStateCurrent}.json`).pipe(
            map((value) => value),
            catchError((error) => {
                if (error.status === 401) {
                    console.log('MockDataService error: ', error);
                }
                return throwError(error);
            })
        );
    }

    /**
     * Get current electrical appliances
     */
    public getElectricalAppliances(offset: number): Observable<any> {
        return this.http
            .get(`assets/data/demo/${constants.demo.files.disaggregationYearMonth}.json`)
            .pipe(
                map((value) => {
                    return {
                        status: 'ok',
                        data: value['data'],
                    };
                }),
                catchError((error) => {
                    if (error.status === 401) {
                        console.log('MockDataService error: ', error);
                    }
                    return throwError(error);
                })
            );
    }

    /**
     *
     * @param offset This describes the used offset in 12 month steps
     */
    public getElectricalAppliancesForMonth(offset: number, month: number): Observable<any> {
        return this.http
            .get(`assets/data/demo/${constants.demo.files.disaggregationYearMonth}.json`)
            .pipe(
                map((value) => {
                    return {
                        status: 'ok',
                        data: value['data'],
                    };
                }),
                catchError((error) => {
                    if (error.status === 401) {
                        console.log('MockDataService error: ', error);
                    }
                    return throwError(error);
                })
            );
    }

    /**
     * Load smart meter information data
     */
    public getInitialize(): Observable<any> {
        let filename;
        if (this.test) {
            filename = constants.demo.files.initializationTest;
        } else {
            filename = constants.demo.files.initialization;
        }

        return this.http.get(`assets/data/demo/${filename}.json`).pipe(
            map((values) => values),
            catchError((error) => {
                if (error.status === 401) {
                    console.log('MockDataService error: ', error);
                }
                return throwError(error);
            })
        );
    }

    /**
     * Get user profile data
     */
    public getProfile(): Observable<any> {
        return this.http.get(`assets/data/demo/${constants.demo.files.profile}.json`).pipe(
            map((values) => values),
            catchError((error) => {
                if (error.status === 401) {
                    console.log('MockDataService error: ', error);
                }
                return throwError(error);
            })
        );
    }

    getMonthlyComparison(): Observable<any> {
        return this.http
            .get(`assets/data/demo/${constants.demo.files.consumptionMonths}.json`)
            .pipe(
                map((values) => {
                    const data = values['data'];
                    return { status: 'ok', data: [data[data.length - 1], data[data.length - 2]] };
                }),
                catchError((error) => {
                    if (error.status === 401) {
                        console.log('MockDataService error: ', error);
                    }
                    return throwError(error);
                })
            );
    }

    getPlugStatus(): Observable<any> {
        let filename: string = constants.demo.files.plugRelay;
        if (this.plugOff) {
            filename = constants.demo.files.plugRelayTest;
        }
        return this.http.get(`assets/data/demo/${filename}.json`).pipe(
            map((values) => values),
            catchError((error) => {
                if (error.status === 401) {
                    console.log('MockDataService error: ', error);
                }
                return throwError(error);
            })
        );
    }

    /**
     * Map values based on the current time
     */
    private mapValues(response, offset, interval, position): any {
        let data = response['data'].results as any[];
        data = data.reverse();

        let num_history_steps = 0;
        switch (position) {
            case 1:
                num_history_steps = ~~((data.length - 300) / 300);
                break;
            case 2:
                num_history_steps = ~~(data.length / 60) - 5;
                break;
            case 3:
                num_history_steps = ~~(data.length / 720);
                break;
            case 4:
                num_history_steps = ~~(data.length / 144) + 1;
                break;
        }

        const start_time_cpy = new Date(this.timestamp_start);
        start_time_cpy.setMinutes(start_time_cpy.getMinutes() - num_history_steps * offset);

        data.forEach((el, idx) => {
            el.timestamp = start_time_cpy.setSeconds(start_time_cpy.getSeconds() + interval);
        });

        return { status: 'ok', data: { results: data, ranges: response.ranges } };
    }

    /**
     * Filter Values by a given timeframe
     */
    private filterByTime(data, offset, limit): any[] {
        const timestamp_start = new Date();
        timestamp_start.setMinutes(timestamp_start.getMinutes() - offset);

        const timestamp_end = new Date();
        timestamp_end.setMinutes(timestamp_end.getMinutes() - limit);

        return data['data'].results.filter((value) => {
            const element_date = new Date(value.timestamp);
            return element_date >= timestamp_start && element_date <= timestamp_end;
        });
    }
}
